/* KUTEÈ waitlist landing — ranked picker + signup.
   - One grid, two ranked picks: step 1 is required, step 2 is optional.
     Tiles are <button aria-pressed>; data-ku-wl-rank is 1 or 2.
   - contact[tags] = base tags, then the step-1 bag, then the step-2 bag —
     PICK order, never tile position (tiles are shuffled before paint).
     The field is rewritten on every change, not only on submit, so
     whatever reads the form at submit time (Shopify's form_submitted →
     Meta Lead pixel) sees the picks no matter which listener runs first.
   - The submit button is never `disabled` before a valid submit (that
     would also block no-JS visitors and hide why); it is styled via
     aria-disabled and the submit handler explains what is missing.
   - Picks are kept in sessionStorage so the success state (rendered after
     Shopify's form round-trip reload) can list them.
   - The Meta Lead is NOT sent here: ku-waitlist-lead.js stages the signup
     and reports it only on the confirmed success page. */
(() => {
  const PICKS_KEY = 'ku_wl_picks';
  const MAX_PICKS = 2;
  const REENABLE_MS = 10000;

  const sessionGet = (key) => {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  };
  const sessionSet = (key, value) => {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (e) {
      /* private mode — non-fatal */
    }
  };

  const validEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((value || '').trim());

  const readPicks = () => {
    try {
      const names = JSON.parse(sessionGet(PICKS_KEY) || '[]');
      return Array.isArray(names) ? names.filter((n) => typeof n === 'string' && n).slice(0, MAX_PICKS) : [];
    } catch (e) {
      return [];
    }
  };

  const initSuccess = (success) => {
    try {
      window.localStorage.setItem('ku_popup_signed', '1'); // never nag a signed-up visitor
    } catch (e) {
      /* non-fatal */
    }
    const box = success.querySelector('[data-ku-wl-picks]');
    const list = success.querySelector('[data-ku-wl-picks-list]');
    const label = success.querySelector('[data-ku-wl-picks-label]');
    const names = readPicks();
    if (!box || !list || names.length === 0) return;
    list.textContent = names.join(' · ');
    if (label && names.length === 1 && label.dataset.one) label.textContent = label.dataset.one;
    box.hidden = false;
  };

  const initPicker = (root) => {
    const tilesWrap = root.querySelector('[data-ku-wl-tiles]');
    const tiles = Array.from(root.querySelectorAll('[data-ku-wl-tile]'));
    const form = root.querySelector('form');
    const tagsField = root.querySelector('[data-ku-wl-tags]');
    const email = root.querySelector('[data-ku-wl-email]');
    const submit = root.querySelector('[data-ku-wl-submit]');
    const panel = root.querySelector('[data-ku-wl-panel]');
    const hint = root.querySelector('[data-ku-wl-hint]');
    const stepLabel = root.querySelector('[data-ku-wl-step-label]');
    const stepHint = root.querySelector('[data-ku-wl-step-hint]');
    const skip = root.querySelector('[data-ku-wl-skip]');
    const status = root.querySelector('[data-ku-wl-status]');
    if (!form || !tagsField || !submit) return;

    const lead = window.kuWaitlistLead; // ku-waitlist-lead.js: Lead only after Shopify confirms the signup
    if (lead) lead.arm(form);

    const base = (tagsField.dataset.base || '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    const rankText = tilesWrap ? [tilesWrap.dataset.rank1 || '', tilesWrap.dataset.rank2 || ''] : ['', ''];
    const order = [];

    /* Email panel: docked once step 1 has a pick — fixed to the bottom of the
       viewport on desktop (with matching page padding), sticky on mobile (CSS).
       Undocking waits for the slide-down animation when there is one. */
    let panelJustOpened = false;
    const measurePanel = () => {
      const height = Math.ceil(panel.getBoundingClientRect().height);
      document.documentElement.style.setProperty('--ku-wl-panel-h', `${height}px`);
    };
    const undockPanel = () => {
      panel.classList.remove('is-sticky', 'is-leaving');
      document.body.classList.remove('ku-wl-panel-open');
    };
    const setPanel = (docked) => {
      if (!panel) return;
      const isDocked = panel.classList.contains('is-sticky');
      const leaving = panel.classList.contains('is-leaving');
      if (docked) {
        if (leaving) panel.classList.remove('is-leaving');
        if (isDocked) return;
        panel.classList.add('is-sticky');
        document.body.classList.add('ku-wl-panel-open');
        measurePanel();
        panelJustOpened = true;
        return;
      }
      if (!isDocked || leaving) return;
      panel.classList.add('is-leaving');
      const animation = window.getComputedStyle(panel).animationName;
      if (!animation || animation === 'none') undockPanel();
    };
    if (panel) {
      panel.addEventListener('animationend', () => {
        if (panel.classList.contains('is-leaving')) undockPanel();
      });
      if ('ResizeObserver' in window) {
        new ResizeObserver(() => {
          if (panel.classList.contains('is-sticky')) measurePanel();
        }).observe(panel);
      }
    }

    const buildTags = () => base.concat(order.map((t) => `waitlist-${t.dataset.handle}`)).join(',');

    const sync = () => {
      tiles.forEach((tile) => {
        const rank = order.indexOf(tile) + 1;
        tile.setAttribute('aria-pressed', rank > 0 ? 'true' : 'false');
        if (rank > 0) tile.dataset.kuWlRank = String(rank);
        else delete tile.dataset.kuWlRank;
        const num = tile.querySelector('[data-ku-wl-rank-num]');
        if (num) num.textContent = rank > 0 ? String(rank) : '';
        const sr = tile.querySelector('[data-ku-wl-rank-text]');
        if (sr) sr.textContent = rank > 0 ? rankText[rank - 1] : '';
      });

      const n = order.length;
      if (stepLabel) {
        const step1 = stepLabel.dataset.step1 || '';
        stepLabel.textContent = n > 0 ? stepLabel.dataset.step2 || step1 : step1;
      }
      if (stepHint) stepHint.hidden = n !== 1;
      if (skip) skip.hidden = n !== 1;
      if (n > 0 && hint) hint.hidden = true;
      setPanel(n > 0);

      tagsField.value = buildTags();
      const ready = n > 0 && validEmail(email ? email.value : '');
      submit.setAttribute('aria-disabled', ready ? 'false' : 'true');
    };

    const toggle = (tile) => {
      const at = order.indexOf(tile);
      if (at !== -1) {
        order.splice(at, 1); // dropping step 1 promotes step 2
      } else if (order.length < MAX_PICKS) {
        order.push(tile);
      } else {
        order[MAX_PICKS - 1] = tile; // a third bag replaces the second pick
      }
      sync();
      const opened = panelJustOpened;
      panelJustOpened = false;
      if (!status) return;
      const rank = order.indexOf(tile) + 1;
      const text =
        rank > 0
          ? [tile.dataset.name, rankText[rank - 1]].filter(Boolean).join(': ')
          : stepLabel
          ? stepLabel.textContent
          : '';
      const announce = opened ? panel.dataset.announce : '';
      status.textContent = announce ? `${text}. ${announce}` : text;
    };

    tiles.forEach((tile) => tile.addEventListener('click', () => toggle(tile)));
    if (skip && email) skip.addEventListener('click', () => email.focus());
    if (email) email.addEventListener('input', sync);

    /* Window + capture phase: Shopify's bot-protection script on customer
       forms takes the submit event first and re-submits the form itself,
       which skipped a plain form listener (an empty pick went through).
       Running first lets us block it and write the tags before it reads them. */
    window.addEventListener(
      'submit',
      (event) => {
        if (event.target !== form) return;
        if (order.length === 0) {
          event.preventDefault();
          event.stopImmediatePropagation();
          if (hint) hint.hidden = false;
          const first = tiles[0];
          if (first) {
            first.scrollIntoView({ behavior: 'smooth', block: 'center' });
            first.focus({ preventScroll: true });
          }
          return;
        }
        // Email format is left to the browser's own type=email validation.
        tagsField.value = buildTags();
        sessionSet(PICKS_KEY, JSON.stringify(order.map((t) => t.dataset.name)));
        if (lead) {
          lead.stage({
            source: 'landing',
            contentName: order[0].dataset.handle,
            secondChoice: order[1] ? order[1].dataset.handle : '',
          });
        }
        /* Disable only after this submission's form data is built (a disabled
           submitter drops out of it). Re-enable if we're still here later —
           e.g. a dismissed bot challenge — so nobody gets stuck. */
        window.setTimeout(() => {
          submit.disabled = true;
          window.setTimeout(() => {
            submit.disabled = false;
          }, REENABLE_MS);
        }, 0);
      },
      true
    );

    window.addEventListener('pageshow', (event) => {
      if (event.persisted) submit.disabled = false;
    });

    sync();
  };

  const init = (root) => {
    if (!root || root.dataset.kuWlInit === 'true') return;
    root.dataset.kuWlInit = 'true';
    const success = root.querySelector('[data-ku-wl-success]');
    if (success) initSuccess(success);
    else initPicker(root);
  };

  const run = () => document.querySelectorAll('[data-ku-waitlist-landing]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
  document.addEventListener('shopify:section:load', run);
})();
